// lodash
import get from 'lodash/get';
import includes from 'lodash/includes';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import {
  PUT_CC_DELIVERY_DETAILS,
  GET_ORDER_CONFIRMATION_PAGE,
  HAND_OVER,
  LOGIN_RESET,
  SAVE_ACCOUNT_DATA,
  SUBMIT_ORDER,
  APPS_GUEST_HANDOVER,
  APPS_AUTHENTICATED_HANDOVER,
  AUTH0_REGISTRATION_CALLBACK,
} from '../../../constants/actionConstants';
import { isZeroAmount } from '../../../utils';
import { getNonGlobalError } from '../../../utils/error/parseError';
// features
import errorCodeConstants from '../../../constants/errorCodeConstants';
import deliveryConstants from '../../../constants/deliveryConstants';
import { COLLECT_PLUS_OWNER_ID } from '../../../constants/clickCollectConstants';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import { checkGvfSlotGreaterThanTwoHours } from '../payment/orderFormReducer';
import getOrderDetails from '../../../utils/helpers/getOrderDetails';

export const INITIAL_STATE = {
  order: {
    id: '',
    deliveryAddress: {
      addressee: {},
      address: {},
      phoneNumber: '',
    },
    billingAddress: {
      addressee: {},
      address: {},
      phoneNumber: '',
    },
    customer: {},
    items: [],
    payments: [],
    deliveries: [],
  },
  promoCodeApplied: {},
  showCreateAccountPrompt: false,
  showSaveOrderPrompt: false,
  isClickCollectOrder: false,
  accountError: {},
  isGiftReceiptOrder: false,
  giftMessageLines: [],
  getOrderConfirmationPageApiCallActive: false,
  isClickCollectHeadOffice: false,
  orderPlacedAfterClickCollectCutOff: false,
};

function getSaveAccountDataError(uncompletedActions, isApps) {
  let errorCode = '';
  if (includes(uncompletedActions, 'savePersonalDetails')) {
    errorCode = isApps
      ? errorCodeConstants.CLIENT_APPS_SAVE_ACCOUNT_DATA_DETAILS_NOT_SAVED
      : errorCodeConstants.CLIENT_SAVE_ACCOUNT_DATA_DETAILS_NOT_SAVED;
  } else if (includes(uncompletedActions, 'savePaymentCard')) {
    errorCode = isApps
      ? errorCodeConstants.CLIENT_APPS_SAVE_PAYMENT_CARD_NOT_SAVED
      : errorCodeConstants.CLIENT_SAVE_PAYMENT_CARD_NOT_SAVED;
  }

  return getNonGlobalError({
    error: { code: errorCode },
  });
}

export const parseDeliveries = (order = {}, deliveryDeliveries = []) => (
  order.deliveries.map((delivery = {}) => {
    const type = delivery.type;
    const giftMessage = get(delivery, 'fulfilment.giftMessageLines', []);
    const giftMessageLines = giftMessage[0] === 'This is a gift receipt' ? [] : giftMessage;

    let isGvfSlotGreaterThanTwoHours = false;
    if (type === deliveryConstants.GREEN_VAN_FLEET) {
      isGvfSlotGreaterThanTwoHours = checkGvfSlotGreaterThanTwoHours(delivery, deliveryDeliveries);
    }

    return {
      ...delivery,
      items: order.items.filter(item => includes(delivery.itemIds, item.id)),
      isGiftReceiptOrder: !isEmpty(giftMessage),
      giftMessageLines,
      isGvfSlotGreaterThanTwoHours,
    };
  })
);

export default function orderConfirmationReducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {

    case `${PUT_CC_DELIVERY_DETAILS}.SUCCESS`: {
      return {
        ...state,
        isClickCollectHeadOffice: action.collectionPoint.isHeadOffice,
      };
    }

    case `${GET_ORDER_CONFIRMATION_PAGE}.LOADING`: {
      return {
        ...state,
        getOrderConfirmationPageApiCallActive: true,
      };
    }

    case `${GET_ORDER_CONFIRMATION_PAGE}.SUCCESS`: {
      const createAccountPrompt = get(action, 'result.createAccountPrompt') || {};

      const isClickCollectOrder = !!get(action, 'result.order.deliveries[0].fulfilment.collectionInfo');
      const order = action.result.order;
      let orderPlacedAfterClickCollectCutOff = false;
      let oldCutOffTime;

      if (isClickCollectOrder && action.selectedCollectionPoint) {
        if (order.deliveries[0].fulfilment.collectionInfo.ownerId === COLLECT_PLUS_OWNER_ID) {
          order.deliveryAddress.address.addressLine1 = action.selectedCollectionPoint.storeName;
        } else {
          if (order.deliveries[0].fulfilment.collectionInfo.collectionDateTime !== action.selectedCollectionTime) {
            orderPlacedAfterClickCollectCutOff = true;
            oldCutOffTime = action.selectedCollectionTime;
          }
          order.deliveryAddress.address.addressLine2 = action.selectedCollectionPoint.storeName;
        }
      }

      const modifiedOrder = {
        ...order,
        deliveries: parseDeliveries(order, action.deliveries),
      };

      return {
        ...state,
        orderDetails: getOrderDetails({
          deliveries: modifiedOrder.deliveries,
          deliveryAddress: modifiedOrder.deliveryAddress,
          billingAddress: modifiedOrder.billingAddress,
          payments: modifiedOrder.payments,
          isClickCollectHeadOffice: state.isClickCollectHeadOffice,
          isClickCollectOrder,
        }),
        getOrderConfirmationPageApiCallActive: false,
        getOrderConfirmationPageApiCallComplete: true,
        order: modifiedOrder,
        promoCodeApplied: get(action, 'result.order.appliedPromotion', {}),
        showCreateAccountPrompt: createAccountPrompt.visible || false,
        isClickCollectOrder,
        orderPlacedAfterClickCollectCutOff,
        oldCutOffTime,
        showSaveOrderPrompt: action.result.saveOrderPrompt?.visible,
        orderInformation: action.result.saveOrderPrompt?.orderInformation,
        expiredSignedIn: action.result.expiredSignedIn || false,
      };
    }

    case `${SAVE_ACCOUNT_DATA}.SUCCESS`: {
      const uncompletedActions = get(action, 'result.uncompletedActions');
      const accountError = uncompletedActions && uncompletedActions.length ?
        getSaveAccountDataError(uncompletedActions, action.isApps) : null;
      return {
        ...state,
        showAccountError: !!accountError,
        accountError,
      };
    }

    case `${SUBMIT_ORDER}.FAILED`:
      return {
        ...state,
        orderPlacedAfterClickCollectCutOff: false,
      };

    case LOGIN_RESET:
    case `${APPS_GUEST_HANDOVER}.SUCCESS`:
    case `${APPS_AUTHENTICATED_HANDOVER}.SUCCESS`:
    case `${HAND_OVER}.SUCCESS`: {
      return { ...INITIAL_STATE };
    }

    case `${AUTH0_REGISTRATION_CALLBACK}.LOADING`: {
      return {
        ...state,
        getOrderConfirmationPageApiCallComplete: false,
      };
    }

    default:
      return state;
  }
}

export const getGiftOptionsRemainingBalance = (state = {}) => {
  const payments = get(state, 'confirmation.order.payments', []);
  const { remainingBalance } =
    find(payments, ({ remainingBalance, type }) => (
      type === paymentTypeConstants.GIFT_VOUCHER && !isZeroAmount(remainingBalance)
    )) || {};

  return remainingBalance;
};
